/**
 * LEAVE-BAL-GROUP-001: employee-grouped Leave Balances table.
 * One parent row per employee (summed totals) with an expandable, indented
 * per-leave-type breakdown. Pagination is handled by the parent (employees).
 */
import React from 'react';
import { ChevronRight } from 'lucide-react';

const NUM_COLS = [
  { key: 'entitled', label: 'Entitled' },
  { key: 'used', label: 'Used' },
  { key: 'pending', label: 'Pending' },
  { key: 'available', label: 'Available' },
  { key: 'carry_forward', label: 'Carry Forward' },
  { key: 'manual_adjustment', label: 'Manual Adjustment' },
];

const fmt = (v) => (v == null ? '—' : Number.isInteger(Number(v)) ? String(v) : Number(v).toFixed(1));

const LeaveBalancesGrouped = ({ rows, expanded, onToggle }) => (
  <div className="border border-gray-200 rounded-lg overflow-x-auto" data-testid="leave-balances-grouped">
    <table className="w-full text-xs">
      <thead className="tf-thead">
        <tr>
          <th className="w-7 px-1 py-2" aria-label="Expand" />
          <th className="px-2 py-2 text-left font-medium text-gray-600">Employee</th>
          <th className="px-2 py-2 text-left font-medium text-gray-600 w-28">Role</th>
          <th className="px-2 py-2 text-left font-medium text-gray-600 w-24">Leave Types</th>
          {NUM_COLS.map(c => (
            <th key={c.key} className="px-2 py-2 text-right font-medium text-gray-600 leading-tight w-20">{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((r, i) => {
          const open = expanded.has(i);
          return (
            <React.Fragment key={r.user_id || i}>
              <tr
                className={`cursor-pointer hover:bg-slate-50 ${open ? 'bg-blue-50/40' : ''}`}
                onClick={() => onToggle(i)}
                aria-expanded={open}
                data-testid={`balance-employee-row-${i}`}
              >
                <td className="px-1 py-2 text-gray-400">
                  <ChevronRight className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-90' : ''}`} data-testid={`balance-employee-toggle-${i}`} />
                </td>
                <td className="px-2 py-2 font-medium text-gray-800" data-testid={`balance-employee-name-${i}`}>{r.employee || '—'}</td>
                <td className="px-2 py-2 text-gray-600">{String(r.role ?? '—').replace(/_/g, ' ')}</td>
                <td className="px-2 py-2 text-gray-600" data-testid={`balance-employee-types-${i}`}>{r.leave_type_count} {r.leave_type_count === 1 ? 'type' : 'types'}</td>
                {NUM_COLS.map(c => (
                  <td key={c.key} className="px-2 py-2 text-right font-medium text-gray-800 tabular-nums" data-testid={`balance-employee-${c.key}-${i}`}>{fmt(r[c.key])}</td>
                ))}
              </tr>
              {open && (
                <tr className="bg-slate-50/70" data-testid={`balance-employee-detail-${i}`}>
                  <td />
                  <td colSpan={3 + NUM_COLS.length} className="px-2 pt-1 pb-3">
                    <div className="ml-4 border-l-2 border-blue-200 pl-3">
                      <table className="w-full text-xs border border-slate-200 rounded-md bg-white">
                        <thead>
                          <tr className="bg-slate-100/80 text-gray-600">
                            <th className="px-2 py-1.5 text-left font-medium">Leave Type</th>
                            {NUM_COLS.map(c => <th key={c.key} className="px-2 py-1.5 text-right font-medium w-24">{c.label}</th>)}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {(r.leave_types || []).map((t, j) => (
                            <tr key={t.leave_type} className="hover:bg-slate-50" data-testid={`balance-type-row-${i}-${j}`}>
                              <td className="px-2 py-1.5 text-gray-700" data-testid={`balance-type-name-${i}-${j}`}>{t.leave_type}</td>
                              {NUM_COLS.map(c => (
                                <td key={c.key} className="px-2 py-1.5 text-right text-gray-700 tabular-nums" data-testid={`balance-type-${c.key}-${i}-${j}`}>{fmt(t[c.key])}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default LeaveBalancesGrouped;
